import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../utils/apiUrl';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  FiSave, FiCreditCard, FiDollarSign,
  FiPercent, FiInfo, FiExternalLink, FiUsers, FiBriefcase
} from 'react-icons/fi';

const API_URL = getApiUrl();

export default function PaymentMethodsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('particuliers');
  
  // Payment methods configuration
  const [paymentConfig, setPaymentConfig] = useState({
    // ============ PARTICULIERS ============
    // MyPOS for CB payments
    mypos_enabled: true,
    mypos_sid: '',
    mypos_wallet_number: '',
    mypos_private_key: '',
    mypos_key_index: '3',
    mypos_test_mode: true,
    mypos_add_fees_to_client: false,
    mypos_fee_percentage: '1.5',
    
    // MyPOS Bank account for wire transfers (Particuliers)
    mypos_bank_enabled: true,
    mypos_bank_name: 'MyPOS',
    mypos_bank_iban: '',
    mypos_bank_bic: '',
    mypos_bank_account_holder: '',
    
    // ============ ENTREPRISES / ASSO ============
    // Crédit Agricole for wire transfers (Entreprises)
    ca_bank_enabled: true,
    ca_bank_name: 'Crédit Agricole',
    ca_bank_iban: '',
    ca_bank_bic: '',
    ca_bank_account_holder: '',
    
    // ============ COMMON ============
    // On-site payment
    onsite_enabled: true,
    onsite_methods: ['cash', 'card'],
    
    // Check
    check_enabled: true,
    check_order_to: '',
    check_address: '',
    
    // Deposit settings
    deposit_enabled: true,
    deposit_percentage: '30',
    deposit_fixed_amount: '100',
    deposit_type: 'percentage',
    deposit_fixed_link: '',
    
    // General settings
    currency: 'EUR',
    tax_rate: '0' // TVA non applicable
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPaymentConfig(prev => ({
          ...prev,
          ...data,
          // Ensure booleans are properly set
          mypos_enabled: data.mypos_enabled !== false,
          mypos_bank_enabled: data.mypos_bank_enabled !== false,
          ca_bank_enabled: data.ca_bank_enabled !== false,
          onsite_enabled: data.onsite_enabled !== false,
          check_enabled: data.check_enabled !== false,
          deposit_enabled: data.deposit_enabled !== false,
          mypos_add_fees_to_client: data.mypos_add_fees_to_client === true
        }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/settings/payments`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentConfig)
      });

      if (response.ok) {
        toast.success('Paramètres de paiement enregistrés');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const PaymentMethodCard = ({ 
    title, 
    description, 
    enabled, 
    onToggle, 
    icon,
    iconBg = 'bg-slate-100',
    badge,
    children 
  }) => (
    <Card className={`transition-all ${enabled ? 'border-green-200 bg-green-50/30' : ''}`}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center`}>
              {icon}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <CardTitle className="text-lg">{title}</CardTitle>
                {badge && <Badge variant="outline" className={badge.className}>{badge.text}</Badge>}
              </div>
              <CardDescription>{description}</CardDescription>
            </div>
          </div>
          <Switch checked={enabled} onCheckedChange={onToggle} />
        </div>
      </CardHeader>
      {enabled && children && (
        <CardContent className="pt-0 border-t">
          <div className="pt-4">
            {children}
          </div>
        </CardContent>
      )}
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payment-methods-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Moyens de Paiement</h1>
          <p className="text-slate-500 mt-1">Configurez les options de paiement par type de client</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
          <FiSave className="mr-2 h-4 w-4" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <FiInfo className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">Configuration par type de client</p>
          <p><strong>Particuliers :</strong> Paiement par CB (MyPOS) ou virement sur compte MyPOS</p>
          <p><strong>Entreprises / Associations :</strong> Virement uniquement sur compte Crédit Agricole</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="particuliers" className="flex items-center gap-2">
            <FiUsers className="h-4 w-4" /> Particuliers
          </TabsTrigger>
          <TabsTrigger value="entreprises" className="flex items-center gap-2">
            <FiBriefcase className="h-4 w-4" /> Entreprises
          </TabsTrigger>
          <TabsTrigger value="deposit">Acompte</TabsTrigger>
          <TabsTrigger value="general">Général</TabsTrigger>
        </TabsList>

        {/* Particuliers Tab */}
        <TabsContent value="particuliers" className="space-y-4 mt-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 rounded-lg">
            <h3 className="font-semibold flex items-center gap-2">
              <FiUsers className="h-5 w-5" /> Modes de paiement pour les Particuliers
            </h3>
            <p className="text-blue-100 text-sm mt-1">
              CB via MyPOS ou virement bancaire sur le compte MyPOS
            </p>
          </div>
          
          {/* MyPOS CB */}
          <PaymentMethodCard
            title="Paiement CB (MyPOS)"
            description="Paiement par carte bancaire via MyPOS"
            enabled={paymentConfig.mypos_enabled}
            onToggle={(v) => setPaymentConfig({ ...paymentConfig, mypos_enabled: v })}
            icon={<FiCreditCard className="h-6 w-6 text-purple-600" />}
            iconBg="bg-purple-100"
            badge={{ text: 'CB', className: 'bg-purple-100 text-purple-700 border-purple-300' }}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Store ID (SID)</Label>
                  <Input
                    value={paymentConfig.mypos_sid}
                    onChange={(e) => setPaymentConfig({ ...paymentConfig, mypos_sid: e.target.value })}
                    placeholder="000000000000010"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Wallet Number</Label>
                  <Input
                    value={paymentConfig.mypos_wallet_number}
                    onChange={(e) => setPaymentConfig({ ...paymentConfig, mypos_wallet_number: e.target.value })}
                    placeholder="61938166610"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label>Clé Privée RSA</Label>
                <Textarea
                  value={paymentConfig.mypos_private_key}
                  onChange={(e) => setPaymentConfig({ ...paymentConfig, mypos_private_key: e.target.value })}
                  placeholder="-----BEGIN RSA PRIVATE KEY-----"
                  rows={4}
                  className="mt-1 font-mono text-xs"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                <div>
                  <Label className="text-base">Mode Test (Sandbox)</Label>
                  <p className="text-sm text-slate-500">Tester sans transactions réelles</p>
                </div>
                <Switch
                  checked={paymentConfig.mypos_test_mode}
                  onCheckedChange={(v) => setPaymentConfig({ ...paymentConfig, mypos_test_mode: v })}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                <div>
                  <Label className="text-base">Ajouter les frais au client</Label>
                  <p className="text-sm text-slate-500">
                    Les frais de transaction ({paymentConfig.mypos_fee_percentage}%) seront ajoutés au montant
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Input
                    value={paymentConfig.mypos_fee_percentage}
                    onChange={(e) => setPaymentConfig({ ...paymentConfig, mypos_fee_percentage: e.target.value })}
                    className="w-20 text-center"
                    type="number"
                    step="0.1"
                  />
                  <span>%</span>
                  <Switch
                    checked={paymentConfig.mypos_add_fees_to_client}
                    onCheckedChange={(v) => setPaymentConfig({ ...paymentConfig, mypos_add_fees_to_client: v })}
                  />
                </div>
              </div>
            </div>
          </PaymentMethodCard>

          {/* MyPOS Bank Transfer */}
          <PaymentMethodCard
            title="Virement Bancaire (Compte MyPOS)"
            description="Virement sur le compte associé à MyPOS"
            enabled={paymentConfig.mypos_bank_enabled}
            onToggle={(v) => setPaymentConfig({ ...paymentConfig, mypos_bank_enabled: v })}
            icon={<FiDollarSign className="h-6 w-6 text-blue-600" />}
            iconBg="bg-blue-100"
            badge={{ text: 'VIREMENT', className: 'bg-blue-100 text-blue-700 border-blue-300' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nom de la banque</Label>
                <Input
                  value={paymentConfig.mypos_bank_name}
                  onChange={(e) => setPaymentConfig({ ...paymentConfig, mypos_bank_name: e.target.value })}
                  placeholder="MyPOS"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Titulaire du compte</Label>
                <Input
                  value={paymentConfig.mypos_bank_account_holder}
                  onChange={(e) => setPaymentConfig({ ...paymentConfig, mypos_bank_account_holder: e.target.value })}
                  placeholder="JUST A SMILE EVENTS"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>IBAN</Label>
                <Input
                  value={paymentConfig.mypos_bank_iban}
                  onChange={(e) => setPaymentConfig({ ...paymentConfig, mypos_bank_iban: e.target.value })}
                  placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                  className="mt-1 font-mono"
                />
              </div>
              <div>
                <Label>BIC / SWIFT</Label>
                <Input
                  value={paymentConfig.mypos_bank_bic}
                  onChange={(e) => setPaymentConfig({ ...paymentConfig, mypos_bank_bic: e.target.value })}
                  placeholder="XXXXXXXX"
                  className="mt-1 font-mono"
                />
              </div>
            </div>
          </PaymentMethodCard>

          {/* On-site Payment for Particuliers */}
          <PaymentMethodCard
            title="Paiement sur Place"
            description="Le client paie le solde le jour de l'événement"
            enabled={paymentConfig.onsite_enabled}
            onToggle={(v) => setPaymentConfig({ ...paymentConfig, onsite_enabled: v })}
            icon={<FiDollarSign className="h-6 w-6 text-green-600" />}
            iconBg="bg-green-100"
          >
            <div className="space-y-3">
              <Label>Moyens acceptés sur place :</Label>
              <div className="flex flex-wrap gap-3">
                {[
                  { id: 'cash', label: 'Espèces' },
                  { id: 'card', label: 'Carte bancaire' },
                  { id: 'check', label: 'Chèque' }
                ].map(method => (
                  <label key={method.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={paymentConfig.onsite_methods?.includes(method.id)}
                      onChange={(e) => {
                        const methods = paymentConfig.onsite_methods || [];
                        if (e.target.checked) {
                          setPaymentConfig({ ...paymentConfig, onsite_methods: [...methods, method.id] });
                        } else {
                          setPaymentConfig({ ...paymentConfig, onsite_methods: methods.filter(m => m !== method.id) });
                        }
                      }}
                      className="w-4 h-4 text-amber-600 rounded"
                    />
                    <span>{method.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </PaymentMethodCard>
        </TabsContent>

        {/* Entreprises Tab */}
        <TabsContent value="entreprises" className="space-y-4 mt-6">
          <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white p-4 rounded-lg">
            <h3 className="font-semibold flex items-center gap-2">
              <FiBriefcase className="h-5 w-5" /> Modes de paiement pour les Entreprises / Associations
            </h3>
            <p className="text-amber-100 text-sm mt-1">
              Virement bancaire uniquement sur le compte Crédit Agricole
            </p>
          </div>
          
          {/* Crédit Agricole Bank Transfer */}
          <PaymentMethodCard
            title="Virement Bancaire (Crédit Agricole)"
            description="Compte dédié aux paiements des entreprises et associations"
            enabled={paymentConfig.ca_bank_enabled}
            onToggle={(v) => setPaymentConfig({ ...paymentConfig, ca_bank_enabled: v })}
            icon={<FiDollarSign className="h-6 w-6 text-green-700" />}
            iconBg="bg-green-100"
            badge={{ text: 'VIREMENT PRO', className: 'bg-green-100 text-green-700 border-green-300' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nom de la banque</Label>
                <Input
                  value={paymentConfig.ca_bank_name}
                  onChange={(e) => setPaymentConfig({ ...paymentConfig, ca_bank_name: e.target.value })}
                  placeholder="Crédit Agricole"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Titulaire du compte</Label>
                <Input
                  value={paymentConfig.ca_bank_account_holder}
                  onChange={(e) => setPaymentConfig({ ...paymentConfig, ca_bank_account_holder: e.target.value })}
                  placeholder="JUST A SMILE EVENTS"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>IBAN</Label>
                <Input
                  value={paymentConfig.ca_bank_iban}
                  onChange={(e) => setPaymentConfig({ ...paymentConfig, ca_bank_iban: e.target.value })}
                  placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                  className="mt-1 font-mono"
                />
              </div>
              <div>
                <Label>BIC / SWIFT</Label>
                <Input
                  value={paymentConfig.ca_bank_bic}
                  onChange={(e) => setPaymentConfig({ ...paymentConfig, ca_bank_bic: e.target.value })}
                  placeholder="AGRIFRPP"
                  className="mt-1 font-mono"
                />
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Note :</strong> Ce compte sera automatiquement proposé pour les réservations de type "Entreprise" ou "Association". 
                Le paiement par CB n'est pas disponible pour ces clients.
              </p>
            </div>
          </PaymentMethodCard>

          {/* Check for Entreprises */}
          <PaymentMethodCard
            title="Chèque"
            description="Paiement par chèque (entreprises)"
            enabled={paymentConfig.check_enabled}
            onToggle={(v) => setPaymentConfig({ ...paymentConfig, check_enabled: v })}
            icon={<FiDollarSign className="h-6 w-6 text-amber-600" />}
            iconBg="bg-amber-100"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>À l'ordre de</Label>
                <Input
                  value={paymentConfig.check_order_to}
                  onChange={(e) => setPaymentConfig({ ...paymentConfig, check_order_to: e.target.value })}
                  placeholder="JUST A SMILE EVENTS"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Adresse d'envoi</Label>
                <Textarea
                  value={paymentConfig.check_address}
                  onChange={(e) => setPaymentConfig({ ...paymentConfig, check_address: e.target.value })}
                  placeholder="23 Av. de la Crau&#10;13118 Istres"
                  rows={2}
                  className="mt-1"
                />
              </div>
            </div>
          </PaymentMethodCard>
        </TabsContent>

        {/* Deposit Tab */}
        <TabsContent value="deposit" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Configuration de l'Acompte</CardTitle>
                  <CardDescription>Définissez le montant de l'acompte demandé à la réservation (valable pour tous les types de clients)</CardDescription>
                </div>
                <Switch
                  checked={paymentConfig.deposit_enabled}
                  onCheckedChange={(v) => setPaymentConfig({ ...paymentConfig, deposit_enabled: v })}
                />
              </div>
            </CardHeader>
            {paymentConfig.deposit_enabled && (
              <CardContent className="space-y-4">
                <div>
                  <Label>Mode de calcul de l'acompte</Label>
                  <Select
                    value={paymentConfig.deposit_type}
                    onValueChange={(v) => setPaymentConfig({ ...paymentConfig, deposit_type: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Pourcentage du total (calculé)</SelectItem>
                      <SelectItem value="fixed_link">Lien MyPOS fixe (100€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {paymentConfig.deposit_type === 'percentage' ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <FiPercent className="h-5 w-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800">Mode pourcentage</p>
                          <p className="text-sm text-amber-700">L'acompte est calculé automatiquement en fonction du montant total de la réservation.</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label>Pourcentage de l'acompte</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="number"
                          value={paymentConfig.deposit_percentage}
                          onChange={(e) => setPaymentConfig({ ...paymentConfig, deposit_percentage: e.target.value })}
                          className="w-24"
                          min="0"
                          max="100"
                        />
                        <span className="text-slate-500">%</span>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">
                        Ex: Pour une réservation de 500€, l'acompte sera de {(500 * (parseFloat(paymentConfig.deposit_percentage) || 0) / 100).toFixed(2)}€
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <FiExternalLink className="h-5 w-5 text-emerald-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-emerald-800">Mode lien fixe MyPOS</p>
                          <p className="text-sm text-emerald-700">Un lien unique de paiement MyPOS pour un acompte fixe de 100€, valable pour toutes les réservations.</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label>Lien de paiement MyPOS</Label>
                      <Input
                        type="url"
                        value={paymentConfig.deposit_fixed_link || ''}
                        onChange={(e) => setPaymentConfig({ ...paymentConfig, deposit_fixed_link: e.target.value })}
                        placeholder="https://mypos.com/vmp/btn/..."
                        className="mt-1"
                      />
                      <p className="text-sm text-slate-500 mt-1">
                        Collez ici le lien de paiement unique MyPOS pour l'acompte fixe de 100€
                      </p>
                    </div>
                    <div>
                      <Label>Montant fixe</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="number"
                          value={paymentConfig.deposit_fixed_amount || '100'}
                          onChange={(e) => setPaymentConfig({ ...paymentConfig, deposit_fixed_amount: e.target.value })}
                          className="w-32"
                          min="0"
                        />
                        <span className="text-slate-500">€</span>
                      </div>
                    </div>
                    {paymentConfig.deposit_fixed_link && (
                      <div className="flex items-center gap-2">
                        <a 
                          href={paymentConfig.deposit_fixed_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <FiExternalLink className="h-4 w-4" />
                          Tester le lien de paiement
                        </a>
                      </div>
                    )}
                  </div>
                )}

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Mode actif :</strong> {paymentConfig.deposit_type === 'percentage' 
                      ? `Acompte de ${paymentConfig.deposit_percentage}% calculé sur le total de la réservation`
                      : `Acompte fixe de ${paymentConfig.deposit_fixed_amount || 100}€ via lien MyPOS unique`
                    }
                  </p>
                </div>
              </CardContent>
            )}
          </Card>
        </TabsContent>

        {/* General Tab */}
        <TabsContent value="general" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Paramètres Généraux</CardTitle>
              <CardDescription>Devise et fiscalité</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Devise</Label>
                  <Select
                    value={paymentConfig.currency}
                    onValueChange={(v) => setPaymentConfig({ ...paymentConfig, currency: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EUR">Euro (€)</SelectItem>
                      <SelectItem value="USD">Dollar ($)</SelectItem>
                      <SelectItem value="GBP">Livre Sterling (£)</SelectItem>
                      <SelectItem value="CHF">Franc Suisse (CHF)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Taux de TVA (%)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number"
                      value={paymentConfig.tax_rate}
                      onChange={(e) => setPaymentConfig({ ...paymentConfig, tax_rate: e.target.value })}
                      className="w-24"
                      min="0"
                      max="100"
                    />
                    <span className="text-slate-500">%</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    0% = TVA non applicable (Article 293 B du CGI)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
